import React, { useEffect, useState } from "react";
import DownsellTrialModal from "./modals/DownsellTrialModal";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

/**
 * App-root downsell host.
 *
 * Mounted ONCE at the top of <AppInner /> so the discounted-trial modal
 * works on every route — Landing (/), AppShell (/app), Pricing (/pricing).
 * Without this, an authenticated user who clicks Back from Stripe lands on
 * /app with no downsell logic mounted (Landing.jsx never gets rendered).
 *
 * Two trigger paths feed the same modal:
 *   1. URL params  → Stripe's cancel_url (?stripe_canceled=1&offer=full_trial&plan=…)
 *   2. sessionStorage hint → set by PricingModal before redirecting to Stripe.
 *      Catches the browser-back gesture (Stripe doesn't fire cancel_url for that).
 */

const HINT_TTL_MS = 30 * 60 * 1000; // 30 minutes

function readDownsellHint(consume) {
  if (typeof window === "undefined") return null;

  // 1. URL params
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_canceled") === "1") {
      const offer = params.get("offer");
      const plan = params.get("plan") || "monthly";
      if (consume) {
        const url = new URL(window.location.href);
        url.searchParams.delete("stripe_canceled");
        url.searchParams.delete("offer");
        url.searchParams.delete("plan");
        window.history.replaceState({}, "", url.toString());
        try { window.sessionStorage.removeItem("stripe_pending_downsell"); } catch (_) { /* ignore */ }
      }
      if (offer === "full_trial") return { plan };
    }
  } catch (_) { /* ignore */ }

  // 2. sessionStorage
  try {
    const raw = window.sessionStorage.getItem("stripe_pending_downsell");
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (consume) window.sessionStorage.removeItem("stripe_pending_downsell");
    if (!data || data.offer !== "full_trial") return null;
    if (typeof data.at !== "number" || Date.now() - data.at > HINT_TTL_MS) return null;
    return { plan: data.plan || "monthly" };
  } catch (_) {
    return null;
  }
}

export default function GlobalDownsellHost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [downsell, setDownsell] = useState(() => {
    const hit = readDownsellHint(true);
    return hit ? { open: false, plan: hit.plan, pending: true } : { open: false, plan: "monthly", pending: false };
  });

  // 2-second grace period before opening the modal (matches the spec).
  useEffect(() => {
    if (!downsell.pending || downsell.open) return undefined;
    const t = setTimeout(() => setDownsell((d) => ({ ...d, open: true, pending: false })), 2000);
    return () => clearTimeout(t);
  }, [downsell.pending, downsell.open]);

  // Catch back-button bfcache restores from Stripe.
  useEffect(() => {
    function onPageShow(e) {
      if (!e.persisted) return;
      const hit = readDownsellHint(true);
      if (hit) setDownsell({ open: false, plan: hit.plan, pending: true });
    }
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Imperative trigger from anywhere in the app (e.g. PricingModal dispatches
  // this when the user closes the pricing modal without checking out).
  // Opens the modal immediately — no 2 s grace period because the user just
  // performed an explicit dismissal action.
  useEffect(() => {
    function onTrigger(e) {
      const plan = (e?.detail?.plan === "yearly") ? "yearly" : "monthly";
      setDownsell({ open: true, plan, pending: false });
    }
    window.addEventListener("stripe:open-downsell", onTrigger);
    return () => window.removeEventListener("stripe:open-downsell", onTrigger);
  }, []);

  return (
    <DownsellTrialModal
      open={downsell.open}
      plan={downsell.plan}
      isAuthed={!!user?.id}
      onClose={() => setDownsell((d) => ({ ...d, open: false }))}
      onSignIn={() => navigate("/", { state: { showAuth: true } })}
    />
  );
}
